import "./CreateInvoiceModal.css";
import "./DiscountType.css";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import {
  getCreateInvoicePopupMetaData,
  getCreateInvoicePopupMetaDataForSaveChanges,
  getCreateInvoicePopupMetaDataValues,
  initialDiscountState,
} from "./ViewOrderUtils";
import { useState, useEffect, KeyboardEvent } from "react";
import { CreateInvoiceModalProps } from "./ViewOrderType";
import Switch from "react-switch";
import DiscountType from "./DiscountType";
import CircularProgress from "@mui/material/CircularProgress";
import { Tooltip } from "@mui/material";
import ScreenOverlay from "../../components/dms_common/ScreenOverlay";
import { truncateToTwoDecimals } from "../../components/dms_common/DmsUtils";
import useShortcutKeys from "../../components/dms_common/ShortcutKeys";
import BigDecimal from "js-big-decimal";

const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({
  modalOpen,
  handleModalClose,
  orderData,
  handleOnSave,
  discount,
  setDiscount,
  invoiceLoading,
  hideAdditionalDistributorDiscount = false,
  orderWillGet,
  hideCouponDiscount,
  finalBillAmountLabel,
  showBothHeaderLevelDiscountedAmountAndPercentage,
  saveInvoiceChanges,
}) => {
  const markDiscountType = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user?.markDiscountType;
  const [discountAmount, setDiscountAmount] = useState<string>("0");
  const [discountPercentage, setDiscountPercentage] = useState<string>("0");
  const [finalAmount, setFinalAmount] = useState<number>(0);

  useEffect(() => {
  const hundred = new BigDecimal(100);
  const one = new BigDecimal(1);
  const orderNetAmount = new BigDecimal(orderData?.netAmount || 0);
  const inputValue = new BigDecimal(truncateToTwoDecimals(Number(discount?.discountValue || "0")));
  let discountValue = new BigDecimal(0);
  let discountPercent = new BigDecimal(0);
    if(!discount.discountType || discount?.discountValue?.length === 0) return;
    if(discount?.discountValue === "0") {
      setDiscountAmount("0");
      setDiscountPercentage("0");
      setFinalAmount(Number(truncateToTwoDecimals(Number(orderNetAmount.getValue()))));
      return;
    }

  if (markDiscountType) {
    if ("markup" === markDiscountType?.toLowerCase()) {
      if (discount?.discountType === "VALUE") {
        // Given: Value, Calculate: Percentage (Reverse Markup)
        // Formula: percentage = ((netAmount / (netAmount - value)) - 1) * 100
        discountValue = inputValue;
        if (parseFloat(orderNetAmount.getValue()) > 0) {
          const denominator = orderNetAmount.subtract(inputValue);
          discountPercent = orderNetAmount.divide(denominator).subtract(one).multiply(hundred);
        } else {
          discountPercent = new BigDecimal(0);
        }
        setDiscountAmount(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountValue.getValue());
        setDiscountPercentage(discountPercent.getValue());
      } else {
        // Given: Percentage, Calculate: Value (Markup)
        // Formula: value = netAmount - (netAmount / (1 + percentage/100))
        discountPercent = inputValue;
        const divisor = one.add(inputValue.divide(hundred));
        discountValue = orderNetAmount.subtract(orderNetAmount.divide(divisor));
        setDiscountAmount(discountValue.getValue());
        setDiscountPercentage(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountPercent.getValue());
      }
    } else { // markdown
      if (discount?.discountType === "VALUE") {
        // Given: Value, Calculate: Percentage (Reverse Standard)
        // Formula: percentage = (value / netAmount) * 100
        discountValue = inputValue;
        if (parseFloat(orderNetAmount.getValue()) > 0) {
          discountPercent = inputValue.divide(orderNetAmount).multiply(hundred);
        } else {
          discountPercent = new BigDecimal(0);
        }
        setDiscountAmount(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountValue.getValue());
        setDiscountPercentage(discountPercent.getValue());
      } else {
        // Given: Percentage, Calculate: Value (Standard)
        // Formula: value = netAmount * (percentage / 100)
        discountPercent = inputValue;
        discountValue = inputValue.divide(hundred).multiply(orderNetAmount);
        setDiscountAmount(discountValue.getValue());
        setDiscountPercentage(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountPercent.getValue());
      }
    }
  } else {
    // No mark discount (default behavior - standard calculation)
    if (discount?.discountType === "VALUE") {
      // Given: Value, Calculate: Percentage (Reverse Standard)
      discountValue = inputValue;
      if (parseFloat(orderNetAmount.getValue()) > 0) {
        discountPercent = inputValue.divide(orderNetAmount).multiply(hundred);
      } else {
        discountPercent = new BigDecimal(0);
      }
      setDiscountAmount(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountValue.getValue());
      setDiscountPercentage(discountPercent.getValue());
    } else {
      // Given: Percentage, Calculate: Value (Standard)
      discountPercent = inputValue;
      discountValue = inputValue.divide(hundred).multiply(orderNetAmount);
      setDiscountAmount(discountValue.getValue());
      setDiscountPercentage(discount?.discountValue?.endsWith(".") ? discount.discountValue : discountPercent.getValue());
    }
  }

  const finalAmount = orderNetAmount.subtract(discountValue);
  setFinalAmount(Number(truncateToTwoDecimals(Number(finalAmount.getValue()))));
}, [orderData, discount, markDiscountType]);

  const handleSwitchChange = (checked: boolean) => {
    if (!checked) {
      setDiscount(initialDiscountState);
    } else {
      setDiscount((prevDiscount) => ({
        ...prevDiscount,
        discountApplied: checked,
      }));
    }
  };

  const handleRadioToggle = (value: "VALUE" | "PERCENTAGE") => {
    setDiscount((prevDiscount) => ({
      ...prevDiscount,
      discountValue: "0",
      discountType: value,
    }));
  };

  const handleDiscountValueChange = (value: string) => {
    setDiscount((prevDiscount) => ({
      ...prevDiscount,
      discountValue: value,
    }));
  };

  const handleResetChanges = () => {
    setDiscount({ ...initialDiscountState, discountApplied: true });
  };

  const reset = () => {
    setDiscount(initialDiscountState);
    setFinalAmount(0);
  };

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const designationBasedUomSecondary = ConfigDictionary.designationBasedUomSecondary;
  let createInvoiceOrderDetailsMetaDataValues: any = {};
  createInvoiceOrderDetailsMetaDataValues = getCreateInvoicePopupMetaDataValues(orderData);

  const designationEnabled  =  designationBasedUomSecondary?.enable ?? false;
  const availableDesignations = designationBasedUomSecondary?.roles?.designationKeys?.map(((item: { type: any; }) => item?.type)) || [];
  const currentDesignation = JSON.parse(localStorage.authContext).user.designation || [];
  const designation = availableDesignations.find((type: any) => 
    currentDesignation.includes(type)
  );
  const designationKeys = designationBasedUomSecondary?.roles?.designationKeys ?? [];
  const matchedRole = designationKeys.find((role: { type: string; uomAllowed: any }) => role.type === designation);

    const handleSwitchKeyDown = (event: KeyboardEvent) => {
    // Check if the pressed key is 'Enter' or ' ' (Space)
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault(); // Prevent default action (like form submission or scrolling)
      handleSwitchChange(!discount?.discountApplied); // Toggle the switch state
    }
  };

    useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "a" && modalOpen) {
        event.preventDefault();
        if (!invoiceLoading) {
          handleOnSave("");
        }
      } else if (event.key === "Escape") {
        event.preventDefault();
        reset();
        handleModalClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleOnSave, invoiceLoading, handleModalClose]);

  

  const { positiveCta } = useShortcutKeys();

  return (
    <Modal
      open={modalOpen}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className="create-invoice-modal-container">
      <ScreenOverlay apply={invoiceLoading} />
        <Tooltip title={orderData?.orderNumber}>
          <div className="createInvoiceModalHeader">
            <p>{saveInvoiceChanges ? `Save Changes for : ${orderData?.invoiceSerialNumber ?? orderData?.invoiceNumber}` : `Create Invoice for : ${orderData?.orderNumber}`}</p>
            <button onClick={() => {
        reset();
        handleModalClose();
      }} style={{ border: "none", background: "none" }}>
            <CloseIcon style={{ cursor: "pointer" }}  />
            </button>
          </div>
        </Tooltip>

        <div className="createInvoice-orderDetails">
          {(saveInvoiceChanges ? getCreateInvoicePopupMetaDataForSaveChanges(finalBillAmountLabel) : getCreateInvoicePopupMetaData(hideCouponDiscount, finalBillAmountLabel))?.map((obj: any) => {
            const { key, value, color, currencySymbol } = obj;
            if (designationEnabled && designation && designationKeys && matchedRole && (key === "QTY(CS)" || key === "QTY(PC)")){
              const elementToKeep = matchedRole?.uomAllowed?.case ? "QTY(CS)" : "QTY(PC)";
              if (elementToKeep !== key){
                return null
              }
            }

            return (
              <div className="createInvoice-orderDetailTile" key={key}>
                <span style={color ? { color: color, fontWeight: 500 } : {}}>{key}</span>
                <span style={color ? { color: color, fontWeight: 600 } : {}}>
                  {currencySymbol ? ConfigDictionary.currency : ""}{" "}
                  {createInvoiceOrderDetailsMetaDataValues?.[value]}
                </span>
              </div>
            );
          })}
        </div>

        {!hideAdditionalDistributorDiscount && (
        <div className="add-discount-container">
          <div className="toggleContainer">
            <p>Additional distributor discount</p>
            <Switch
              onKeyDown={handleSwitchKeyDown}
              onChange={handleSwitchChange}
              checked={discount?.discountApplied}
              offColor="#9ba09f"
              onColor="#00C6B1"
              handleDiameter={16.2}
              uncheckedIcon={false}
              checkedIcon={false}
              height={20.7}
              width={42.6}
            />
          </div>

          {discount?.discountApplied && (
            <div className="discount-configure-container">
              <DiscountType
                type="Value"
                handleRadioToggle={() => handleRadioToggle("VALUE")}
                discount={discount}
                handleDiscountValueChange={handleDiscountValueChange}
                maxVal={orderData?.netAmount}
                discountAmount={discountAmount}
                discountPercentage={discountPercentage}
                showBothHeaderLevelDiscountedAmountAndPercentage={showBothHeaderLevelDiscountedAmountAndPercentage}
              />
              <DiscountType
                type="Percentage"
                handleRadioToggle={() => handleRadioToggle("PERCENTAGE")}
                discount={discount}
                labelSuffix="%"
                handleDiscountValueChange={handleDiscountValueChange}
                maxVal={100}
                discountAmount={discountAmount}
                discountPercentage={discountPercentage}
                showBothHeaderLevelDiscountedAmountAndPercentage={showBothHeaderLevelDiscountedAmountAndPercentage}
              />
            </div>
          )}

          {discount?.discountApplied && (
            <div className="final-amount-container">
              <p>
                Final amount (after additional discount):{" "}
                <span>
                  {ConfigDictionary.currency}
                  {finalAmount}
                </span>
              </p>
            </div>
          )}
        </div>
      )}

        <div className="createInvoice-footerContainer" style={{justifyContent: "flex-end"}}>
          {
            (orderWillGet !== "INVOICED") && 
            <span style={{color: "red", fontSize: "12px", fontStyle: "italic", width: "100%", alignItems: "start"}}>
              {
                (orderWillGet === "REJECTED") ?
                <div style={{display: "flex", flexDirection: "column", alignItems: "start"}}>
                  <span>Order will get rejected due to unavailability of stock.</span>
                  <span>Do you wish to proceed?</span>
                </div>
                : (orderWillGet === "PARTIALLY INVOICED") ?
                <div style={{display: "flex", flexDirection: "column", alignItems: "start"}}>
                  <span>Order will be partially invoiced due to partial availability of stock.</span>
                  <span>Do you wish to proceed?</span>
                </div>
                :
                <></>
              }
            </span>
          }
          <div style={{display: "flex", gap: "10px", whiteSpace: "nowrap"}}>
          {+discount?.discountValue > 0 && (
            <button className="invoice-reset-changes" onClick={handleResetChanges}>
              <span>Reset</span>
            </button>
          )}
          <button
            className="invoice-reset-changes"
            style={{ backgroundColor: "#00C6B1", borderColor: "#00C6B1" }}
            onClick={() => handleOnSave("")}
            disabled={invoiceLoading}
          >
            <span
              style={{
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {invoiceLoading ? (
                <CircularProgress
                  style={{
                    width: 20,
                    height: 20,
                    color: "#fff",
                  }}
                />
              ) : (
                saveInvoiceChanges ? "Save" : "Proceed"
              )}
            </span>
            {!invoiceLoading && <span style={{fontSize:"9.5px", color: "#fff"}}>{positiveCta}</span>}
          </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateInvoiceModal;
